import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { BoxIcon } from 'lucide-react';
interface MetricCardProps {
  value: string;
  label: string;
  description?: string;
  suffix?: string;
  prefix?: string;
  icon?: BoxIcon;
  gradient?: 'blue' | 'amber' | 'green';
}
export function MetricCard({
  value,
  label,
  description,
  suffix = '',
  prefix = '',
  icon: Icon,
  gradient = 'blue'
}: MetricCardProps) {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-50px'
  });
  const numericValue = parseFloat(value.replace(/[^0-9.]/g, ''));
  const isNumber = !isNaN(numericValue);
  const [displayValue, setDisplayValue] = useState(0);
  useEffect(() => {
    if (isInView && isNumber) {
      const duration = 2000;
      const steps = 60;
      const stepValue = numericValue / steps;
      let current = 0;
      const timer = setInterval(() => {
        current += stepValue;
        if (current >= numericValue) {
          setDisplayValue(numericValue);
          clearInterval(timer);
        } else {
          setDisplayValue(current);
        }
      }, duration / steps);
      return () => clearInterval(timer);
    }
  }, [isInView, isNumber, numericValue]);
  const gradients = {
    blue: 'bg-gradient-to-br from-blue-50 to-blue-100/50',
    amber: 'bg-gradient-to-br from-amber-50 to-amber-100/50',
    green: 'bg-gradient-to-br from-green-50 to-green-100/50'
  };
  const iconColors = {
    blue: 'text-blue-600',
    amber: 'text-amber-600',
    green: 'text-green-600'
  };
  const textColors = {
    blue: 'text-blue-600',
    amber: 'text-amber-600',
    green: 'text-green-600'
  };
  return <motion.div ref={ref} whileHover={{
    y: -4
  }} transition={{
    duration: 0.2
  }} className={`flex flex-col items-center text-center p-6 ${gradients[gradient]} rounded-2xl border border-slate-200/50 shadow-sm hover:shadow-md transition-shadow duration-300`} data-id="element-147">
      {Icon && <div className="mb-3" data-id="element-148">
          <Icon className={`w-10 h-10 ${iconColors[gradient]}`} strokeWidth={1.5} data-id="element-149" />
        </div>}

      <div className={`mb-2 flex items-baseline justify-center ${textColors[gradient]}`} data-id="element-150">
        {prefix && <span className="text-2xl font-bold mr-1" data-id="element-151">{prefix}</span>}
        <span className="text-4xl md:text-5xl font-bold tracking-tight" data-id="element-152">
          {isNumber ? value.includes('.') ? displayValue.toFixed(1) : Math.round(displayValue).toLocaleString() : value}
        </span>
        {suffix && <span className="text-2xl font-bold ml-1" data-id="element-153">{suffix}</span>}
      </div>

      <h3 className="text-base font-bold text-slate-900 mb-1" data-id="element-154">{label}</h3>
      {description && <p className="text-sm text-slate-600 leading-snug" data-id="element-155">{description}</p>}
    </motion.div>;
}